import { useState, type FormEvent } from 'react';
import type { DayCountConvention, Loan, LenderReportedFigures, PreEmiInterestTreatment, RepaymentArrangement } from '@/types';
import { createDraftLoan, updateLoan, markLoanAsLicConfirmed } from '@/lib/db';
import { validateLoanInput } from '@/lib/calc/validation';
import { calculateRoundedEmi } from '@/lib/calc/emi';
import { CurrencyInput } from '@/components/CurrencyInput';

interface LoanFormProps {
  existingLoan?: Loan;
  onSaved: () => void;
}

/**
 * A single form used both for FIRST-TIME onboarding (no existingLoan —
 * creates a new draft_expected loan) and for later EDITING of the base
 * loan terms (existingLoan provided — updates in place). Per spec, the
 * app must start with one editable Draft/Expected loan record built from
 * skeleton terms and clearly label it as such; this form is where that
 * happens and where the user can later mark it lic_confirmed once real
 * sanction terms are known.
 *
 * PHASE 1 CORRECTION — two pieces of derived-field behaviour live here:
 *
 * 1. Approved Amount defaults to (and keeps tracking) Sanction Amount
 *    until the user manually edits it to something different, at which
 *    point it "detaches" and the user can explicitly reset it back to
 *    match Sanction Amount. This mirrors the Loan record's
 *    `isApprovedAmountManuallySet` flag.
 *
 * 2. Original EMI is auto-calculated from Approved Amount + Rate +
 *    Tenure using the standard EMI formula (src/lib/calc/emi.ts) any
 *    time those three inputs change, UNLESS the user has manually
 *    overridden it — mirroring `isEmiManuallyOverridden`. The user can
 *    switch back to "calculated" mode at any time, which discards
 *    the manual override and recalculates immediately.
 */
export function LoanForm({ existingLoan, onSaved }: LoanFormProps) {
  const isEditing = !!existingLoan;

  const [lender, setLender] = useState(existingLoan?.lender ?? 'LIC Housing Finance Ltd');
  const [loanAccountNumber, setLoanAccountNumber] = useState(existingLoan?.loanAccountNumber ?? '');

  const [sanctionAmount, setSanctionAmount] = useState<number | undefined>(existingLoan?.sanctionAmount);

  const [approvedAmount, setApprovedAmount] = useState<number | undefined>(existingLoan?.approvedAmount);
  const [isApprovedAmountManuallySet, setIsApprovedAmountManuallySet] = useState(
    existingLoan?.isApprovedAmountManuallySet ?? false
  );

  const [originalAnnualRatePercent, setOriginalAnnualRatePercent] = useState(
    existingLoan?.originalAnnualRatePercent?.toString() ?? ''
  );
  const [originalTenureMonths, setOriginalTenureMonths] = useState(
    existingLoan?.originalTenureMonths?.toString() ?? ''
  );

  const [originalEmiAtFullDisbursement, setOriginalEmiAtFullDisbursement] = useState<number | undefined>(
    existingLoan?.originalEmiAtFullDisbursement
  );
  const [isEmiManuallyOverridden, setIsEmiManuallyOverridden] = useState(
    existingLoan?.isEmiManuallyOverridden ?? false
  );

  const [emiDayOfMonth, setEmiDayOfMonth] = useState(existingLoan?.emiDayOfMonth?.toString() ?? '7');
  const [loanStartDate, setLoanStartDate] = useState(existingLoan?.loanStartDate ?? '');
  const [regularEmiStartDate, setRegularEmiStartDate] = useState(existingLoan?.regularEmiStartDate ?? '');
  const [repaymentArrangement, setRepaymentArrangement] = useState<RepaymentArrangement | ''>(existingLoan?.repaymentArrangement ?? '');
  const [regularEmiAmount, setRegularEmiAmount] = useState<number | undefined>(existingLoan?.regularEmiAmount);
  const [dayCountConvention, setDayCountConvention] = useState<DayCountConvention>(existingLoan?.dayCountConvention ?? 'actual_365_fixed');
  const [preEmiInterestTreatment, setPreEmiInterestTreatment] = useState<PreEmiInterestTreatment>(
    existingLoan?.preEmiInterestTreatment ?? 'pay_as_you_go'
  );
  const lr = existingLoan?.lenderReported;
  const [lrAsOf, setLrAsOf] = useState(lr?.asOfDate ?? '');
  const [lrInstalment, setLrInstalment] = useState<number | undefined>(lr?.instalmentAmount);
  const [lrPrincipal, setLrPrincipal] = useState<number | undefined>(lr?.outstandingPrincipal);
  const [lrMaturity, setLrMaturity] = useState(lr?.contractualMaturityDate ?? '');
  const [lrUnpaid, setLrUnpaid] = useState(lr?.unpaidInstalments?.toString() ?? '');
  const [lrLastEmi, setLrLastEmi] = useState<number | undefined>(lr?.lastInstalmentAmount);
  const [notes, setNotes] = useState(existingLoan?.notes ?? '');

  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ---------------------------------------------------------------------
  // TEMPORARY DIAGNOSTIC (Development / Testing) — remove once the
  // Sanction→Approved/EMI runtime bug investigation is resolved. Pure
  // instrumentation: does not alter any calculation, validation, event,
  // or persistence behavior. See handleSanctionAmountChange and
  // recalculateEmiIfNotOverridden below for where the counters increment.
  // ---------------------------------------------------------------------
  const [diagSanctionHandlerHits, setDiagSanctionHandlerHits] = useState(0);
  const [diagRecalcHits, setDiagRecalcHits] = useState(0);

  const emiRoundingRule = existingLoan?.emiRoundingRule ?? 'nearest_paisa';

  /**
   * Recalculates EMI from current approvedAmount/rate/tenure, but only
   * applies it if the user hasn't manually taken over the EMI field.
   * Called after any change to the three EMI-driving inputs.
   */
  function recalculateEmiIfNotOverridden(
    nextApprovedAmount: number | undefined,
    nextRateText: string,
    nextTenureText: string
  ) {
    setDiagRecalcHits((n) => n + 1); // TEMPORARY DIAGNOSTIC
    if (isEmiManuallyOverridden) return;
    const rate = Number(nextRateText);
    const tenure = Number(nextTenureText);
    if (nextApprovedAmount === undefined || !Number.isFinite(rate) || !Number.isFinite(tenure)) {
      setOriginalEmiAtFullDisbursement(undefined);
      return;
    }
    const calculated = calculateRoundedEmi(nextApprovedAmount, rate, tenure, emiRoundingRule);
    setOriginalEmiAtFullDisbursement(calculated);
  }

  function handleSanctionAmountChange(next: number | undefined) {
    setDiagSanctionHandlerHits((n) => n + 1); // TEMPORARY DIAGNOSTIC
    setSanctionAmount(next);
    if (!isApprovedAmountManuallySet) {
      // Approved Amount keeps tracking Sanction Amount until the user
      // manually diverges it — see field-level docs on Loan.approvedAmount.
      setApprovedAmount(next);
      recalculateEmiIfNotOverridden(next, originalAnnualRatePercent, originalTenureMonths);
    }
  }

  function handleApprovedAmountChange(next: number | undefined) {
    setApprovedAmount(next);
    // Any manual edit to this field detaches it from Sanction Amount,
    // UNLESS the new value happens to exactly equal the current sanction
    // amount (in which case there's nothing to "diverge" from yet).
    const nowManuallySet = next !== sanctionAmount;
    setIsApprovedAmountManuallySet(nowManuallySet);
    recalculateEmiIfNotOverridden(next, originalAnnualRatePercent, originalTenureMonths);
  }

  function handleResetApprovedToSanctioned() {
    setApprovedAmount(sanctionAmount);
    setIsApprovedAmountManuallySet(false);
    recalculateEmiIfNotOverridden(sanctionAmount, originalAnnualRatePercent, originalTenureMonths);
  }

  function handleRateChange(next: string) {
    setOriginalAnnualRatePercent(next);
    recalculateEmiIfNotOverridden(approvedAmount, next, originalTenureMonths);
  }

  function handleTenureChange(next: string) {
    setOriginalTenureMonths(next);
    recalculateEmiIfNotOverridden(approvedAmount, originalAnnualRatePercent, next);
  }

  function handleEmiManualChange(next: number | undefined) {
    setOriginalEmiAtFullDisbursement(next);
    setIsEmiManuallyOverridden(true);
  }

  function handleUseCalculatedEmi() {
    setIsEmiManuallyOverridden(false);
    const rate = Number(originalAnnualRatePercent);
    const tenure = Number(originalTenureMonths);
    if (approvedAmount === undefined || !Number.isFinite(rate) || !Number.isFinite(tenure)) {
      setOriginalEmiAtFullDisbursement(undefined);
      return;
    }
    setOriginalEmiAtFullDisbursement(calculateRoundedEmi(approvedAmount, rate, tenure, emiRoundingRule));
  }

  function buildLenderReported(): LenderReportedFigures | undefined {
    const figures: LenderReportedFigures = {
      asOfDate: lrAsOf || undefined,
      instalmentAmount: lrInstalment,
      outstandingPrincipal: lrPrincipal,
      contractualMaturityDate: lrMaturity || undefined,
      unpaidInstalments: lrUnpaid.trim() === '' ? undefined : Number(lrUnpaid),
      lastInstalmentAmount: lrLastEmi,
    };
    return Object.values(figures).some((v) => v !== undefined) ? figures : undefined;
  }

  function buildInput(): Partial<Loan> {
    return {
      lender: lender.trim(),
      loanAccountNumber: loanAccountNumber.trim() || undefined,
      sanctionAmount,
      approvedAmount,
      isApprovedAmountManuallySet,
      originalAnnualRatePercent: Number(originalAnnualRatePercent),
      originalTenureMonths: Number(originalTenureMonths),
      originalEmiAtFullDisbursement,
      isEmiManuallyOverridden,
      emiDayOfMonth: Number(emiDayOfMonth),
      loanStartDate: loanStartDate || undefined,
      regularEmiStartDate: regularEmiStartDate || undefined,
      repaymentArrangement: repaymentArrangement || undefined,
      regularEmiAmount,
      dayCountConvention,
      preEmiInterestTreatment,
      lenderReported: buildLenderReported(),
      notes: notes.trim() || undefined,
    };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const input = buildInput();
    const validationErrors = validateLoanInput(input);
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateLoan(existingLoan.id, input);
      } else {
        await createDraftLoan(
          input as Pick<
            Loan,
            | 'lender'
            | 'sanctionAmount'
            | 'approvedAmount'
            | 'originalAnnualRatePercent'
            | 'originalTenureMonths'
            | 'originalEmiAtFullDisbursement'
          > &
            Partial<Loan>
        );
      }
      onSaved();
    } catch (err) {
      setErrors([err instanceof Error ? err.message : 'Something went wrong saving the loan.']);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleConfirmLic() {
    if (!existingLoan) return;
    setIsSaving(true);
    try {
      // Persist any edits made in the form first, then flip status.
      const input = buildInput();
      const validationErrors = validateLoanInput(input);
      setErrors(validationErrors);
      if (validationErrors.length > 0) return;
      await updateLoan(existingLoan.id, input);
      await markLoanAsLicConfirmed(existingLoan.id);
      onSaved();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form className="stack" onSubmit={handleSubmit}>
      {!isEditing && (
        <div className="card" style={{ borderColor: 'var(--color-warning)' }}>
          <p style={{ margin: 0 }}>
            <span className="badge badge-draft">Draft / Expected</span>
            <br />
            <br />
            Since you don't yet have an LIC sanction letter or KFS, enter your best current understanding of the
            loan terms below. This will be clearly labelled as a draft everywhere in the app until you confirm it
            against real LIC documents.
          </p>
        </div>
      )}

      {errors.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--color-danger)' }}>
          {errors.map((err) => (
            <p key={err} className="field-error" style={{ margin: 0 }}>
              {err}
            </p>
          ))}
        </div>
      )}

      <div className="field-group">
        <label htmlFor="lender">Lender</label>
        <input id="lender" value={lender} onChange={(e) => setLender(e.target.value)} />
      </div>

      <div className="field-group">
        <label htmlFor="loanAccountNumber">Loan Account Number (optional, if known)</label>
        <input
          id="loanAccountNumber"
          value={loanAccountNumber}
          onChange={(e) => setLoanAccountNumber(e.target.value)}
          placeholder="Not yet known"
        />
      </div>

      <div className="field-group">
        <label htmlFor="sanctionAmount">Sanction Amount</label>
        <CurrencyInput id="sanctionAmount" value={sanctionAmount} onChange={handleSanctionAmountChange} />
      </div>

      <div className="field-group">
        <label htmlFor="approvedAmount">Approved Amount</label>
        <CurrencyInput id="approvedAmount" value={approvedAmount} onChange={handleApprovedAmountChange} />
        {isApprovedAmountManuallySet ? (
          <span className="field-hint">
            Manually set — no longer automatically matches Sanction Amount.{' '}
            <button
              type="button"
              onClick={handleResetApprovedToSanctioned}
              style={{ background: 'none', border: 'none', color: 'var(--color-accent)', padding: 0, cursor: 'pointer' }}
            >
              Reset to match Sanction Amount
            </button>
          </span>
        ) : (
          <span className="field-hint">Automatically matches Sanction Amount unless you change it.</span>
        )}
      </div>

      <div className="row">
        <div className="field-group">
          <label htmlFor="rate">Original / Expected Interest Rate (% p.a.)</label>
          <input
            id="rate"
            type="number"
            step="0.01"
            inputMode="decimal"
            value={originalAnnualRatePercent}
            onChange={(e) => handleRateChange(e.target.value)}
          />
        </div>
        <div className="field-group">
          <label htmlFor="tenure">Original Tenure (months)</label>
          <input
            id="tenure"
            type="number"
            inputMode="numeric"
            value={originalTenureMonths}
            onChange={(e) => handleTenureChange(e.target.value)}
          />
        </div>
      </div>

      <div className="field-group">
        <label htmlFor="emi">Original EMI at Full Disbursement</label>
        <CurrencyInput id="emi" value={originalEmiAtFullDisbursement} onChange={handleEmiManualChange} />
        {isEmiManuallyOverridden ? (
          <span className="field-hint">
            <span className="badge badge-actual_lic" style={{ marginRight: 6 }}>
              Manually Entered
            </span>{' '}
            Use this if you have an actual LIC-confirmed EMI that differs from the calculated value.{' '}
            <button
              type="button"
              onClick={handleUseCalculatedEmi}
              style={{ background: 'none', border: 'none', color: 'var(--color-accent)', padding: 0, cursor: 'pointer' }}
            >
              Use calculated value instead
            </button>
          </span>
        ) : (
          <span className="field-hint">
            <span className="badge badge-projected" style={{ marginRight: 6 }}>
              Calculated
            </span>{' '}
            Automatically calculated from Approved Amount, Rate, and Tenure using the standard EMI formula. This is
            still a projected/draft figure — edit it directly if LIC has confirmed a different EMI.
          </span>
        )}
      </div>

      <div className="field-group">
        <label htmlFor="emiDay">EMI Day of Month</label>
        <input
          id="emiDay"
          type="number"
          min={1}
          max={28}
          value={emiDayOfMonth}
          onChange={(e) => setEmiDayOfMonth(e.target.value)}
        />
      </div>

      <div className="row">
        <div className="field-group">
          <label htmlFor="loanStartDate">Loan Start Date (optional)</label>
          <input
            id="loanStartDate"
            type="date"
            value={loanStartDate}
            onChange={(e) => setLoanStartDate(e.target.value)}
          />
        </div>
        <div className="field-group">
          <label htmlFor="regularEmiStartDate">Regular EMI Start Date (optional)</label>
          <input
            id="regularEmiStartDate"
            type="date"
            value={regularEmiStartDate}
            onChange={(e) => setRegularEmiStartDate(e.target.value)}
          />
        </div>
      </div>
      <span className="field-hint">
        Automatic: the first regular EMI is the first EMI date after this date. If you choose a specific assumption
        below, regular EMI applies from this date. Leave blank to let the app estimate it.
      </span>

      <details className="card-supporting">
        <summary style={{ cursor: 'pointer' }}>Repayment assumptions &amp; LIC-reported figures (optional)</summary>
      <div className="field-group">
        <label htmlFor="repaymentArrangement">Repayment assumption</label>
        <select
          id="repaymentArrangement"
          value={repaymentArrangement}
          onChange={(e) => setRepaymentArrangement(e.target.value as RepaymentArrangement | '')}
        >
          <option value="">Automatic (estimate)</option>
          <option value="interest_only_then_regular">Interest-only before the start date, regular EMI from it</option>
          <option value="regular_from_start">Regular EMI from the start date</option>
        </select>
        <span className="field-hint">
          Automatic: if the loan is not fully disbursed by the first EMI date, that payment is interest-only and regular
          EMI starts the next month. Editable once LIC HFL confirms.
        </span>
      </div>

      <div className="field-group">
        <label htmlFor="regularEmiAmount">Regular EMI Amount (optional, if known)</label>
        <CurrencyInput id="regularEmiAmount" value={regularEmiAmount} onChange={setRegularEmiAmount} />
        <span className="field-hint">
          Leave blank to estimate it. Never replaces the Original EMI above.
        </span>
      </div>

      <div className="card-supporting">
        <div className="card-title">Calculation conventions</div>
        <div className="field-group">
          <label htmlFor="dayCount">Day-count convention</label>
          <select id="dayCount" value={dayCountConvention} onChange={(e) => setDayCountConvention(e.target.value as DayCountConvention)}>
            <option value="actual_365_fixed">Actual / 365 (fixed)</option>
            <option value="actual_365_25">Actual / 365.25</option>
            <option value="actual_360">Actual / 360</option>
          </select>
          <span className="field-hint">Actual/365 matches an observed LIC HFL pre-EMI figure but is not stated in the sanction letter.</span>
        </div>
        <div className="field-group">
          <label htmlFor="preEmiTreatment">Pre-EMI interest</label>
          <select
            id="preEmiTreatment"
            value={preEmiInterestTreatment}
            onChange={(e) => setPreEmiInterestTreatment(e.target.value as PreEmiInterestTreatment)}
          >
            <option value="pay_as_you_go">Paid as interest-only (principal unchanged)</option>
            <option value="capitalize_at_emi_start">Deferred and added to principal at EMI start</option>
          </select>
        </div>
      </div>

      <div className="card-supporting">
        <div className="card-title">Reported by LIC HFL (optional observations)</div>
        <span className="field-hint">Shown as reported; never used to overwrite calculated figures.</span>
        <div className="field-group">
          <label htmlFor="lrAsOf">Observed on</label>
          <input id="lrAsOf" type="date" value={lrAsOf} onChange={(e) => setLrAsOf(e.target.value)} />
        </div>
        <div className="field-group">
          <label htmlFor="lrInstalment">Current instalment shown as payable</label>
          <CurrencyInput id="lrInstalment" value={lrInstalment} onChange={setLrInstalment} />
        </div>
        <div className="field-group">
          <label htmlFor="lrPrincipal">Outstanding principal</label>
          <CurrencyInput id="lrPrincipal" value={lrPrincipal} onChange={setLrPrincipal} />
        </div>
        <div className="field-group">
          <label htmlFor="lrMaturity">Contractual maturity date</label>
          <input id="lrMaturity" type="date" value={lrMaturity} onChange={(e) => setLrMaturity(e.target.value)} />
        </div>
        <div className="row">
          <div className="field-group">
            <label htmlFor="lrUnpaid">Unpaid instalments</label>
            <input id="lrUnpaid" type="number" inputMode="numeric" value={lrUnpaid} onChange={(e) => setLrUnpaid(e.target.value)} />
          </div>
          <div className="field-group">
            <label htmlFor="lrLastEmi">Last EMI payable</label>
            <CurrencyInput id="lrLastEmi" value={lrLastEmi} onChange={setLrLastEmi} />
          </div>
        </div>
      </div>
      </details>

      <div className="field-group">
        <label htmlFor="notes">Notes (optional)</label>
        <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {/* ------------------------------------------------------------- */}
      {/* TEMPORARY DIAGNOSTIC — Development / Testing only.             */}
      {/* Remove once the Sanction→Approved/EMI runtime bug is          */}
      {/* resolved. Read-only, does not affect save/validation/calc.    */}
      {/* ------------------------------------------------------------- */}
      <div className="card" style={{ borderColor: 'var(--color-danger)' }}>
        <div className="card-title">Development / Testing — Temporary Diagnostic</div>
        <div className="stack" style={{ gap: 4 }}>
          <DiagRow label="isApprovedAmountManuallySet" value={String(isApprovedAmountManuallySet)} />
          <DiagRow label="isEmiManuallyOverridden" value={String(isEmiManuallyOverridden)} />
          <DiagRow label="sanctionAmount (numeric)" value={String(sanctionAmount)} />
          <DiagRow label="approvedAmount (numeric)" value={String(approvedAmount)} />
          <DiagRow label="originalEmiAtFullDisbursement (numeric)" value={String(originalEmiAtFullDisbursement)} />
          <DiagRow label="handleSanctionAmountChange hits" value={String(diagSanctionHandlerHits)} />
          <DiagRow label="recalculateEmiIfNotOverridden hits" value={String(diagRecalcHits)} />
        </div>
        <p className="field-hint" style={{ marginTop: 8, marginBottom: 0 }}>
          This panel is temporary instrumentation to diagnose a reported runtime issue. It will be removed once the
          issue is resolved.
        </p>
      </div>

      <button type="submit" className="btn btn-primary btn-block" disabled={isSaving}>
        {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Draft Loan Record'}
      </button>

      {isEditing && existingLoan.status === 'draft_expected' && (
        <button type="button" className="btn btn-block" disabled={isSaving} onClick={handleConfirmLic}>
          Save &amp; Mark as LIC-Confirmed
        </button>
      )}
    </form>
  );
}

/**
 * TEMPORARY DIAGNOSTIC helper — a plain label/value row used only by the
 * Development / Testing panel above. Remove alongside that panel once
 * the runtime bug investigation is resolved.
 */
function DiagRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', fontSize: '0.85rem' }}>
      <span className="text-muted">{label}</span>
      <span className="mono">{value}</span>
    </div>
  );
}

